import React, { useState } from "react";
import { UI, FONT} from "../constants/tokens";
import Icon from "./Icon";
import { Amount } from "./Money";
import { modalScrim, modalPanel, MODAL_PAD_X } from "./Modal";

// ─── Alerts Tab ───────────────────────────────────────────────────────────────
function AlertsDrawer({ forSymbol, alerts, triggered, portfolio, watchlist, cur, onAdd, onRemove, onClose }) {
  const [sym,    setSym]    = useState(forSymbol || "");
  const [dir,    setDir]    = useState("above");
  const [target, setTarget] = useState("");
  const [showList, setShowList] = useState(alerts.length > 0 && !forSymbol);
  const allStocks = [...portfolio.map(s => s.symbol), ...watchlist.map(s => s.symbol)];

  const inp = { background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "10px 12px", color: UI.text.primary, fontSize: 16, outline: "none", flex: 1, fontFamily: FONT.body };

  // Waits for the save and says when it failed (sprint 14, 4.4): the insert
  // used to fail in silence, and the form cleared as if it had worked.
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const add = async () => {
    if (!sym || !target || saving) return;
    setSaving(true); setSaveFailed(false);
    const ok = await onAdd({ id: Date.now(), symbol: sym.toUpperCase(), direction: dir, targetPrice: +target });
    setSaving(false);
    if (ok === false) { setSaveFailed(true); return; }
    setTarget("");
    setShowList(true);
  };

  const allPortfolio = [...portfolio, ...watchlist];

  return (
    <div dir="rtl" style={modalScrim(310, { align: "bottom", padding: 0 })}
      onClick={onClose}>
      {/* A sheet with its own header/body split, so the panel keeps no padding
          of its own and each section applies the shared gutter instead. */}
      <div style={{ ...modalPanel({ variant: "sheet", width: 560, pad: false }), paddingBottom: 28 }}
        onClick={e => e.stopPropagation()}>

        {/* Handle bar */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: UI.fill.subtle }}/>
        </div>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `8px ${MODAL_PAD_X}px 16px` }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: 18, color: UI.text.primary }}>
              <Icon name="bell" size={18} color={UI.text.secondary}/>{forSymbol ? `התראת מחיר · ${forSymbol}` : "ניהול התראות מחיר"}
            </div>
            {forSymbol && <div style={{ fontSize: 14, color: UI.text.muted, marginTop: 2 }}>הגדר התראה כשהמחיר מגיע ליעד</div>}
          </div>
          <button onClick={onClose} aria-label="סגור" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, padding: "8px 10px", color: UI.text.secondary, cursor: "pointer" }}><Icon name="x" size={16} color={UI.text.secondary}/></button>
        </div>

        {/* Triggered alerts banner */}
        {triggered.length > 0 && (
          <div style={{ margin: "0 20px 16px", background: UI.fill.subtle, border: `1px solid ${UI.line.strong}`, borderRadius: 6, padding: "10px 14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: UI.text.primary, fontWeight: 800, marginBottom: 6 }}><Icon name="bell" size={15} color={UI.text.primary}/>{triggered.length === 1 ? "התראה אחת שהמחיר שלה הגיע לסף" : <>{triggered.length} התראות שהמחיר שלהן הגיע לסף</>}</div>
            {triggered.map(a => (
              <div key={a.id} style={{ fontSize: 14, color: UI.text.primary, marginBottom: 3 }}>
                <strong>{a.symbol}</strong> · {a.direction === "above" ? "עלה מעל" : "ירד מתחת ל-"} ${a.targetPrice}
              </div>
            ))}
          </div>
        )}

        {/* Form */}
        <div style={{ padding: "0 22px 16px", display: "flex", flexDirection: "column", gap: 10, borderBottom: `1px solid ${UI.line.hairline}` }}>
          <select value={sym} onChange={e => setSym(e.target.value)} style={{ ...inp, flex: "unset" }}>
            <option value="">בחר מניה...</option>
            {allStocks.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <select value={dir} onChange={e => setDir(e.target.value)} style={{ ...inp, flex: "unset", width: 140 }}>
              <option value="above">מחיר מעל ↑</option>
              <option value="below">מחיר מתחת ↓</option>
            </select>
            <input value={target} onChange={e => setTarget(e.target.value)} placeholder="מחיר להתראה ($)" type="number" style={inp}/>
          </div>
          <button onClick={add} disabled={!sym || !target || saving}
            style={{ background: sym && target ? UI.accent.base : UI.fill.subtle, border: "none", borderRadius: 6, padding: "12px", color: sym && target ? UI.surface.page : UI.text.muted, fontWeight: 800, fontSize: 16, cursor: sym && target ? "pointer" : "not-allowed" }}>
            {saving ? "שומר…" : "+ הוסף התראה"}
          </button>
          {saveFailed && <div role="alert" style={{ fontSize: 13.5, color: UI.text.primary }}>ההתראה לא נשמרה · נסה שוב</div>}
          {/* How an alert arrives, as the function does it (check-price-alerts). */}
          <div style={{ fontSize: 12.5, color: UI.text.muted, lineHeight: 1.6 }}>
            המחיר נבדק כל 15 דקות לפי ציטוט <span dir="ltr" style={{ unicodeBidi: "isolate" }}>Finnhub</span>. כשהוא עובר את הסף נשלחת הודעה פעם אחת למכשירים שבהם הופעלו התראות בהגדרות. בלי מכשיר כזה, ההתראה מסומנת כאן כל עוד המחיר מעבר לסף והאפליקציה פתוחה.
          </div>
        </div>

        {/* List toggle link */}
        <button onClick={() => setShowList(v => !v)}
          style={{ background: "none", border: "none", padding: "14px 22px 0", fontSize: 14, color: UI.text.secondary, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <Icon name="list" size={16} color={UI.text.secondary}/> רשימת ההתראות שלך {alerts.length > 0 && `(${alerts.length})`}
          <span aria-hidden="true" style={{ display: "inline-flex" }}><Icon name={showList ? "chevronUp" : "chevronDown"} size={15} color={UI.text.muted}/></span>
        </button>

        {/* Alerts list */}
        {showList && (
          <div style={{ padding: "10px 22px 0", display: "flex", flexDirection: "column", gap: 8 }}>
            {alerts.length === 0 && (
              <div style={{ textAlign: "center", padding: "22px 0", color: UI.text.muted, fontSize: 14 }}>אין התראות פעילות עדיין</div>
            )}
            {alerts.map(a => {
              const s   = allPortfolio.find(x => x.symbol === a.symbol);
              // A null price (quote not in yet) is not "below target" — `null <= x` would say it is.
              const trg = s && s.currentPrice > 0 && (a.direction === "above" ? s.currentPrice >= a.targetPrice : s.currentPrice <= a.targetPrice);
              return (
                <div key={a.id} style={{ background: trg ? UI.fill.subtle : UI.surface.page, border: `1px solid ${trg ? UI.line.strong : UI.line.hairline}`, borderRadius: 6, padding: "12px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 6, flexShrink: 0, background: UI.fill.subtle, border: `1px solid ${trg ? UI.line.strong : UI.line.hairline}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17 }}>
                    <Icon name={trg ? "bell" : "bellOff"} size={17} color={trg ? UI.text.primary : UI.text.secondary}/>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: UI.text.primary }}>{a.symbol}</div>
                    <div style={{ fontSize: 14, color: UI.text.secondary }}>{a.direction === "above" ? "מעל " : "מתחת ל-"}<Amount value={Number(a.targetPrice)} currency="USD" decimals={2} size={14} weight={600} color={UI.text.secondary}/></div>
                    {s && <div style={{ fontSize: 13, color: UI.text.muted }}>נוכחי: {s.currentPrice > 0 ? <Amount value={s.currentPrice} currency="USD" decimals={2} size={13} weight={600} color={UI.text.secondary}/> : "אין ציטוט עדכני"}</div>}
                  </div>
                  <button onClick={() => onRemove(a.id)} style={{ background: UI.cautionSoft, border: `1px solid ${UI.caution}`, color: UI.caution, borderRadius: 6, width: 30, height: 30, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


export { AlertsDrawer };
export default AlertsDrawer;
